import { DuoMeasurementValues, LineStripMeasureValues, ObjectId, View } from "@novorender/api";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { vec3 } from "gl-matrix";

import { type RootState } from "app";
import { Picker, resetView, selectBookmark, setViewpoint } from "features/render";
import { PointSnapInfo } from "features/render/measureSnap";

import { isPointLineClosed } from "./utils";

export type PointLinePoint = {
    id: string;
    position: vec3;
    snap: PointSnapInfo;
    /**
     * Required to do per-axis measurements.
     */
    objectId?: ObjectId;
};

export type PointLine = {
    id: string;
    points: PointLinePoint[];
    result: undefined | LineStripMeasureValues;
    reversed: boolean;
    /**
     * Overall point line feature measures distances between points,\
     * but when we have only two points in the line - also measure per axis distances.
     */
    firstTwoPointAxisMeasurements?: DuoMeasurementValues;
};

export enum PointLineMode {
    Default,
    Horizontal,
    Vertical,
}

export type PointLineDraggingPoint = {
    pointLineIdx: number;
    pointIdx: number;
    /**
     * Picker when the dragging started,
     * so we can restore it after the drag ends.
     */
    originalPicker: Picker;
};

export type PointLineSnapToPoint = {
    pointLineIdx: number;
    pointIdx: number;
};

const pointLine = (id?: string): PointLine => ({
    id: id ?? crypto.randomUUID(),
    points: [],
    result: undefined,
    reversed: false,
});

const initialState = {
    pointLines: [pointLine()],
    currentIndex: 0 as number,
    mode: PointLineMode.Default,
    draggingPoint: null as null | PointLineDraggingPoint,
    snapToPoint: null as null | number,
    showPointHeightOnCanvas: true,
    showPointAngleOnCanvas: true,
    /**
     * Snapshot of pointLines + currentIndex captured at the start of the current edit session,
     * used to revert all in-session changes on cancel. `null` when no edit session is active.
     */
    editSessionStartSnapshot: null as PointLineEditSessionSnapshot | null,
};

export type PointLineEditSessionSnapshot = {
    pointLines: PointLine[];
    currentIndex: number;
};

type State = typeof initialState;

export const pointLineSlice = createSlice({
    name: "pointLine",
    initialState: initialState,
    reducers: {
        addPoint: {
            reducer: (
                state,
                {
                    payload: { point: pt, pointLineId, pointId, wasLineReversed = false },
                    meta: { view },
                }: PayloadAction<
                    {
                        point: Omit<PointLinePoint, "id">;
                        pointLineId?: string;
                        pointId: string;
                        wasLineReversed?: boolean;
                    },
                    string,
                    { view: View }
                >,
            ) => {
                let current: PointLine;
                if (pointLineId) {
                    const line = state.pointLines.find((l) => l.id === pointLineId);
                    if (line) {
                        current = line;
                    } else {
                        const emptyIdx = state.pointLines.findIndex((l) => l.points.length === 0);
                        if (emptyIdx >= 0) {
                            current = state.pointLines[emptyIdx];
                            current.id = pointLineId;
                            state.currentIndex = emptyIdx;
                        } else {
                            state.pointLines.push(pointLine(pointLineId));
                            state.currentIndex = state.pointLines.length - 1;
                            current = state.pointLines[state.currentIndex];
                        }
                    }
                } else {
                    current = state.pointLines[state.currentIndex];
                }
                const prevPt = current.points.at(-1);

                const pointWithId: PointLinePoint = {
                    ...applyMode(pt as PointLinePoint, state.mode, prevPt),
                    id: pointId,
                };
                if (wasLineReversed !== undefined && wasLineReversed !== current.reversed) {
                    current.points.unshift(pointWithId);
                } else {
                    current.points.push(pointWithId);
                }
                current.result = view.measure?.core.measureLineStrip(current.points.map((p) => p.position));
                current.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (
                point: Omit<PointLinePoint, "id">,
                view: View,
                pointId: string,
                pointLineId?: string,
                wasLineReversed: boolean = false,
            ) => {
                return { payload: { point, pointLineId, pointId, wasLineReversed }, meta: { view } };
            },
        },
        movePoint: {
            reducer: (
                state,
                {
                    payload: { point, lineIdx, pointIdx },
                    meta: { view },
                }: PayloadAction<{ point: PointLinePoint; lineIdx: number; pointIdx: number }, string, { view: View }>,
            ) => {
                const current = state.pointLines[lineIdx];
                const prevPt = current.points[pointIdx - 1];
                const isClosed = isPointLineClosed(current.points);

                point = { ...applyMode(point, state.mode, prevPt), id: current.points[pointIdx].id };
                current.points[pointIdx] = point;
                if (pointIdx === 0 && isClosed) {
                    current.points[current.points.length - 1] = point;
                }
                current.result = view.measure?.core.measureLineStrip(current.points.map((p) => p.position));
                current.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (payload: { point: PointLinePoint; lineIdx: number; pointIdx: number }, view: View) => {
                return { payload, meta: { view } };
            },
        },
        connectPoints: {
            reducer: (
                state,
                { payload: { pointId }, meta: { view } }: PayloadAction<{ pointId: string }, string, { view: View }>,
            ) => {
                const current = state.pointLines[state.currentIndex];

                if (current.points.length < 2) {
                    return state;
                }

                current.points.push({ ...current.points[0], id: pointId });
                current.result = measureLineStrip(view, current.points);
                current.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (view: View, pointId: string) => {
                return { payload: { pointId }, meta: { view } };
            },
        },
        undoPoint: {
            reducer: (state, { meta: { view } }: PayloadAction<void, string, { view: View }>) => {
                const current = state.pointLines[state.currentIndex];
                current.points.pop();
                current.result = measureLineStrip(view, current.points);
                current.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (view: View) => {
                return { payload: undefined, meta: { view } };
            },
        },
        clearCurrent: (state) => {
            state.pointLines[state.currentIndex] = pointLine();
        },
        clearCurrentIfSinglePoint: (state) => {
            if (state.pointLines[state.currentIndex].points.length === 1) {
                state.pointLines[state.currentIndex] = pointLine();
            }
        },
        setCurrent: (state, action: PayloadAction<number>) => {
            state.currentIndex = action.payload;
        },
        clear: () => {
            return initialState;
        },
        toggleLockElevation: (state) => {
            state.mode = state.mode === PointLineMode.Horizontal ? PointLineMode.Default : PointLineMode.Horizontal;
        },
        toggleLockVertical: (state) => {
            state.mode = state.mode === PointLineMode.Vertical ? PointLineMode.Default : PointLineMode.Vertical;
        },
        newPointLine: (state) => {
            if (state.pointLines[state.currentIndex].points.length <= 1) {
                state.pointLines[state.currentIndex] = pointLine();
            } else {
                if (state.pointLines[state.pointLines.length - 1].points.length) {
                    state.pointLines.push(pointLine());
                }
                state.currentIndex = state.pointLines.length - 1;
            }
            state.mode = PointLineMode.Default;
        },
        /**
         * Start a new independent line without mutating existing lines.
         * Used by mobile DPAD flow when exiting transparent mode: keep the
         * current anchor point in its line, but deselect it and continue on a fresh line.
         */
        startDetachedPointLine: (state) => {
            const lastIdx = state.pointLines.length - 1;
            if (lastIdx < 0 || state.pointLines[lastIdx].points.length > 0) {
                state.pointLines.push(pointLine());
                state.currentIndex = state.pointLines.length - 1;
            } else {
                state.currentIndex = lastIdx;
            }
            state.mode = PointLineMode.Default;
        },
        deletePointline: (state, action: PayloadAction<number>) => {
            state.currentIndex = action.payload;
            if (state.pointLines.length > 1) {
                state.pointLines.splice(action.payload, 1);
            } else {
                state.pointLines = [pointLine()];
            }
            state.currentIndex = state.pointLines.length - 1;
        },
        deletePointlineById: (state, action: PayloadAction<string>) => {
            const idx = state.pointLines.findIndex((l) => l.id === action.payload);
            if (idx < 0) {
                return;
            }
            if (state.pointLines.length > 1) {
                state.pointLines.splice(idx, 1);
            } else {
                state.pointLines = [pointLine()];
            }
            state.currentIndex = state.pointLines.length - 1;
        },
        deletePointById: {
            reducer: (
                state,
                action: PayloadAction<{ pointLineId: string; pointId: string }, string, { view: View }>,
            ) => {
                const { pointLineId, pointId } = action.payload;
                const { view } = action.meta;
                const lineIdx = state.pointLines.findIndex((l) => l.id === pointLineId);
                const pointLine = lineIdx >= 0 ? state.pointLines[lineIdx] : undefined;
                if (!pointLine) {
                    return;
                }
                const pointIdx = pointLine.points.findIndex((p) => p.id === pointId);
                if (pointIdx < 0) {
                    return;
                }
                pointLine.points.splice(pointIdx, 1);
                if (pointLine.points.length === 0) {
                    state.pointLines.splice(lineIdx, 1);
                    if (state.currentIndex >= state.pointLines.length) {
                        state.currentIndex = state.pointLines.length - 1;
                    }
                } else {
                    pointLine.result = measureLineStrip(view, pointLine.points);
                    pointLine.firstTwoPointAxisMeasurements = undefined;
                }
            },
            prepare: (payload: { pointLineId: string; pointId: string }, view: View) => {
                return { payload, meta: { view } };
            },
        },
        insertPoint: {
            reducer: (
                state,
                action: PayloadAction<
                    { pointLineId: string; pointIdx: number; point: PointLinePoint; wasLineReversed?: boolean },
                    string,
                    { view: View }
                >,
            ) => {
                const { pointLineId, pointIdx, point, wasLineReversed } = action.payload;
                const { view } = action.meta;
                const line = state.pointLines.find((l) => l.id === pointLineId);
                if (!line || pointIdx < 0 || pointIdx > line.points.length) {
                    return;
                }
                if (line.reversed !== undefined && line.reversed !== wasLineReversed) {
                    line.points.splice(line.points.length - pointIdx, 0, point);
                } else {
                    line.points.splice(pointIdx, 0, point);
                }
                line.result = measureLineStrip(view, line.points);
                line.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (
                payload: { pointLineId: string; pointIdx: number; point: PointLinePoint; wasLineReversed?: boolean },
                view: View,
            ) => ({
                payload,
                meta: { view },
            }),
        },
        restorePointline: (state, action: PayloadAction<PointLine>) => {
            const line = action.payload;
            state.pointLines.push(line);
            state.currentIndex = state.pointLines.length - 1;
        },
        deletePoint: {
            reducer: (
                state,
                action: PayloadAction<{ pointLineIdx: number; pointIdx: number }, string, { view: View }>,
            ) => {
                const { pointLineIdx, pointIdx } = action.payload;
                const { view } = action.meta;
                const pointLine = state.pointLines[pointLineIdx];
                if (!pointLine || pointIdx < 0 || pointIdx >= pointLine.points.length) {
                    return;
                }
                pointLine.points.splice(pointIdx, 1);
                if (pointLine.points.length === 0) {
                    state.pointLines.splice(pointLineIdx, 1);
                    if (state.currentIndex >= state.pointLines.length) {
                        state.currentIndex = state.pointLines.length - 1;
                    }
                }
                pointLine.result = measureLineStrip(view, pointLine.points);
                pointLine.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (payload: { pointLineIdx: number; pointIdx: number }, view: View) => {
                return { payload, meta: { view } };
            },
        },
        reversePoints: {
            reducer: (state, action: PayloadAction<number, string, { view: View }>) => {
                const pointLine = state.pointLines[action.payload];
                const { view } = action.meta;
                pointLine.points = pointLine.points.toReversed();
                pointLine.reversed = !pointLine.reversed;
                pointLine.result = measureLineStrip(view, pointLine.points);
                pointLine.firstTwoPointAxisMeasurements = undefined;
            },
            prepare: (payload: number, view: View) => {
                return { payload, meta: { view } };
            },
        },
        setPointLines: (state, action: PayloadAction<State["pointLines"]>) => {
            state.pointLines = action.payload;
        },
        setDraggingPoint: (state, action: PayloadAction<State["draggingPoint"]>) => {
            state.draggingPoint = action.payload;
        },
        setShowPointHeightOnCanvas: (state, action: PayloadAction<boolean>) => {
            state.showPointHeightOnCanvas = action.payload;
        },
        setShowPointAngleOnCanvas: (state, action: PayloadAction<boolean>) => {
            state.showPointAngleOnCanvas = action.payload;
        },
        setFirstTwoPointAxisMeasurements: (state, action: PayloadAction<(DuoMeasurementValues | undefined)[]>) => {
            state.pointLines.forEach((pointLine, i) => {
                pointLine.firstTwoPointAxisMeasurements = action.payload[i];
            });
        },
        setPointLineSnapToPoint: (state, action: PayloadAction<number | null>) => {
            state.snapToPoint = action.payload;
        },
        /** Idempotent: captures a snapshot only if none has been taken yet. */
        captureEditSessionStartSnapshot: (state) => {
            if (state.editSessionStartSnapshot) {
                return;
            }
            state.editSessionStartSnapshot = {
                pointLines: state.pointLines as PointLine[],
                currentIndex: state.currentIndex,
            };
        },
        clearEditSessionStartSnapshot: (state) => {
            state.editSessionStartSnapshot = null;
        },
        setEditSessionStartSnapshot: (state, action: PayloadAction<State["editSessionStartSnapshot"]>) => {
            state.editSessionStartSnapshot = action.payload;
        },
        /**
         * If a snapshot exists, revert `pointLines` and `currentIndex` to it and clear the snapshot.
         * No-op when no snapshot is active.
         */
        restoreEditSessionStartSnapshot: (state) => {
            const snapshot = state.editSessionStartSnapshot;
            if (!snapshot) {
                return;
            }
            state.pointLines = snapshot.pointLines;
            state.currentIndex = snapshot.currentIndex;
            state.editSessionStartSnapshot = null;
        },
    },
    extraReducers(builder) {
        builder.addCase(selectBookmark, (state, action) => {
            const pointLine = action.payload.measurements.pointLine;
            const view = action.meta.view;

            if ("points" in pointLine) {
                state.pointLines = [
                    {
                        id: crypto.randomUUID(),
                        points: pointLine.points.map((p) => ({
                            id: crypto.randomUUID(),
                            position: p,
                            snap: { type: "vertex", connection: p } as PointSnapInfo,
                        })),
                        result: view.measure?.core.measureLineStrip(pointLine.points),
                        reversed: false,
                    },
                ];
            } else {
                state.pointLines = pointLine.pointLines.map((bmPoints) => {
                    const points = bmPoints.map((p) =>
                        "position" in p
                            ? {
                                  ...p,
                                  id: crypto.randomUUID(),
                                  snap: (p.snap
                                      ? { ...p.snap, connection: p.position }
                                      : { type: "vertex", connection: p.position }) as PointSnapInfo,
                              }
                            : {
                                  id: crypto.randomUUID(),
                                  position: p,
                                  snap: { type: "vertex", connection: p } as PointSnapInfo,
                              },
                    );
                    return {
                        id: crypto.randomUUID(),
                        points,
                        result: measureLineStrip(view, points),
                        reversed: false,
                    };
                });
            }
            if (state.pointLines.length === 0) {
                state.pointLines = initialState.pointLines;
            }
            state.currentIndex = state.pointLines.length - 1;
        });
        builder.addCase(setViewpoint, (state, action) => {
            const pointLineData = action.payload.measurements?.point_line;
            const view = action.meta.view;
            const { publicIdsToObjectIds } = action.meta;

            if (!pointLineData) {
                if (action.meta.ignoreUndefined) {
                    return;
                }
                state.pointLines = [pointLine()];
                state.currentIndex = 0;
                return;
            }

            state.pointLines = pointLineData.point_lines.map((vpPoints) => {
                const points: PointLinePoint[] = vpPoints.map((p) => ({
                    id: crypto.randomUUID(),
                    position: p.position as vec3,
                    objectId: p.object_id ? publicIdsToObjectIds.get(p.object_id) : undefined,
                    snap: (p.snap
                        ? { ...p.snap, connection: p.position }
                        : { type: "vertex", connection: p.position }) as PointSnapInfo,
                }));
                return {
                    id: crypto.randomUUID(),
                    points,
                    result: measureLineStrip(view, points),
                    reversed: false,
                };
            });
            if (state.pointLines.length === 0) {
                state.pointLines = [pointLine()];
            }
            state.currentIndex = state.pointLines.length - 1;
        });
        builder.addCase(resetView, (state) => {
            state.pointLines = [pointLine()];
            state.currentIndex = 0;
        });
    },
});

function measureLineStrip(view: View, points: PointLinePoint[]): LineStripMeasureValues | undefined {
    return view.measure?.core.measureLineStrip(points.map((p) => p.position));
}

function applyMode(point: PointLinePoint, mode: PointLineMode, prevPt?: PointLinePoint): PointLinePoint {
    if (!prevPt || mode === PointLineMode.Default) {
        return point;
    } else if (mode === PointLineMode.Horizontal) {
        return {
            ...point,
            position: [point.position[0], point.position[1], prevPt.position[2]],
        };
    } else {
        return {
            ...point,
            position: [prevPt.position[0], prevPt.position[1], point.position[2]],
        };
    }
}

export const selectPointLines = (state: RootState) => state.pointLine.pointLines;
export const selectCurrentPointLine = (state: RootState) => state.pointLine.pointLines[state.pointLine.currentIndex];
export const selectLockPointLineElevation = (state: RootState) => state.pointLine.mode === PointLineMode.Horizontal;
export const selectLockPointLineVertical = (state: RootState) => state.pointLine.mode === PointLineMode.Vertical;
export const selectPointLineCurrentIdx = (state: RootState) => state.pointLine.currentIndex;
export const selectDraggingPoint = (state: RootState) => state.pointLine.draggingPoint;
export const selectShowPointHeightOnCanvas = (state: RootState) => state.pointLine.showPointHeightOnCanvas;
export const selectShowPointAngleOnCanvas = (state: RootState) => state.pointLine.showPointAngleOnCanvas;
export const selectPointLineSnapToPoint = (state: RootState) => state.pointLine.snapToPoint;
export const selectPointLineEditSessionStartSnapshot = (state: RootState) => state.pointLine.editSessionStartSnapshot;

const { actions, reducer } = pointLineSlice;
export { actions as pointLineActions, reducer as pointLineReducer };
